import * as THREE from 'three';
import { avalancheLayout, caveLayout, hexToWorld, laneFlow, looseLayout, slideShadow, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { COMPANY_RED } from '../models/sendas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * The avalanche's places (docs/sendas/guerrero.md, chapter 4, «El alud»), drawn on the Spine's snow (engine/spine.ts
 * repaints the board and lights it, always cold, by day).
 *
 * - `loose` (act 1, and the opening at the slope's foot): the climb's trail between scree and grey ice, boulders on it
 *   for cover, the ice cave's mouth at its top; each boulder's lee a still, colder shade on the snow, darker while a
 *   front's shadow is out.
 * - `slideFar` (the cinematic): the same slope seen from its foot, huge and white, the crack across the snow up high,
 *   the pass's split ice above.
 * - `avalanche` (act 3, the test) and `caveMouth` (the scenes at the cave): an open slope seven lanes wide, furrows
 *   between them, the void at its sides; nine boulders in three rows, each with its grain (a pale band of quartz
 *   leaning to a side, a chevron in the snow under it the same way); the iron wedges driven into them (a red rag tied
 *   on, red-hot when the snow breaks one); at the foot the cave's mouth, an arch of blue ice, the stretcher with the
 *   nine shields inside.
 * - `iceCave` (act 2): inside the blue ice: walls of it, frost on the floor, the doors shut by ice that crack open
 *   when a room is cleared; cold lights in the ice.
 *
 * The avalanche itself is a shader, never particles: one sheet over the board (the terrain's own geometry) paints the
 * shadow (by lane and row, from the sim's wedges, as `laneFlow` says) and the snow coming down behind the front's
 * line; one long strip stands at the line, the rolling crest of powder. Rock lee from a mask texture. The giant's rocks:
 * a small pool of shadows and stones, as at the fort. No lights of its own (docs/RENDIMIENTO.md): glows.
 * Draw calls: the dressing (one merged mesh), the sheet, the crest, the wedges (one instanced mesh), the doors (three),
 * the stones' pool, a glow layer.
 */

export const SLIDE_PLACES = new Set(['loose', 'slideFar', 'avalanche', 'caveMouth', 'iceCave']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const ROW = 0.866 * S;
const SNOW = new THREE.Color('#eef2f8');
const SNOW_SHADE = new THREE.Color('#b9c4d3');
const ICE = ['#a9c6dc', '#8fb3cf', '#c4daea', '#7ea4c4'];
const ICE_DEEP = ['#3d5f86', '#4a6f97', '#335476', '#577ca3'];
const ROCK = ['#4a5361', '#3e4653', '#56606e', '#353c48'];
const QUARTZ = '#dfe6ee';
const IRON = '#3b3f46';
const WOOD = ['#3a3430', '#4a4038', '#2e2925'];

/** The sheet over the board: the rocks' lee (a mask), the avalanche's shadow by lane and row, the snow behind the front. */
const sheetMat = (R: number): { mat: THREE.ShaderMaterial; tex: THREE.DataTexture; data: Uint8Array } => {
  const W = 2 * R + 1;
  const data = new Uint8Array(W * W * 4);
  const tex = new THREE.DataTexture(data, W, W, THREE.RGBAFormat);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
    uniforms: {
      uTime: { value: 0 },
      uLee: { value: tex },
      uR: { value: R },
      uHex: { value: S },
      uLanes: { value: new Array(28).fill(1) },
      uRows: { value: new THREE.Vector3(-99, -98, -97) },
      uX0: { value: -99 },
      uLaneW: { value: 30 },
      uTop: { value: -20 },
      uFoot: { value: 20 },
      uFront: { value: -999 },
      uShadow: { value: 0 },
      uHide: { value: 0 },
      uLoose: { value: 1 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      varying float vFog;
      void main() {
        vec4 w = modelMatrix * vec4(position + normal * 0.04, 1.0);
        vXZ = w.xz;
        vec4 mv = viewMatrix * w;
        vFog = clamp(-mv.z / 90.0, 0.0, 1.0);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform sampler2D uLee;
      uniform float uR;
      uniform float uHex;
      uniform float uLanes[28];
      uniform vec3 uRows;
      uniform float uX0;
      uniform float uLaneW;
      uniform float uTop;
      uniform float uFoot;
      uniform float uFront;
      uniform float uShadow;
      uniform float uHide;
      uniform float uLoose;
      varying vec2 vXZ;
      varying float vFog;
      // (Wrapped: with large arguments the sin hash loses precision and the noise shows in blocks, docs/RENDIMIENTO.md.)
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      float lane(int seg, float x) {
        if (uLoose > 0.5) return 1.0;
        float k = floor((x - uX0) / uLaneW);
        if (k < 0.0 || k > 6.0) return 0.0;
        return uLanes[seg * 7 + int(k)];
      }
      // How much snow comes down here: by lane below the rows above, eased across each row (the branches turning).
      float thick(vec2 p) {
        if (p.y < uTop || p.y > uFoot + 0.6) return 0.0;
        int seg = p.y < uRows.x ? 0 : p.y < uRows.y ? 1 : p.y < uRows.z ? 2 : 3;
        float t = lane(seg, p.x);
        float band = 0.9;
        for (int i = 0; i < 3; i++) {
          float rz = i == 0 ? uRows.x : i == 1 ? uRows.y : uRows.z;
          float d = p.y - rz;
          if (abs(d) < band) {
            float k = smoothstep(-band, band, d);
            t = mix(lane(i, p.x), lane(i + 1, p.x), k);
          }
        }
        // Soft lane edges: the snow narrows toward the furrows.
        if (uLoose < 0.5) {
          float f = fract((p.x - uX0) / uLaneW);
          t *= smoothstep(0.0, 0.16, f) * smoothstep(1.0, 0.84, f) * 0.6 + 0.4;
        }
        return t;
      }
      void main() {
        float rf = vXZ.y / (uHex * 0.8660254);
        float qf = vXZ.x / uHex - rf * 0.5;
        float sf = -qf - rf;
        float q = floor(qf + 0.5), r = floor(rf + 0.5), s = floor(sf + 0.5);
        float dq = abs(q - qf), dr = abs(r - rf), ds = abs(s - sf);
        if (dq > dr && dq > ds) q = -r - s; else if (dr > ds) r = -q - s;
        vec2 uv = (vec2(q, r) + uR + 0.5) / (2.0 * uR + 1.0);
        float lee = (abs(q) <= uR && abs(r) <= uR) ? texture2D(uLee, uv).r : 0.0;
        float t = thick(vXZ);
        // The shadow: a cold blue-grey veil down every lane the snow will take (white snow on white snow would not
        // read), thicker where it gathers, its streaks sliding downhill.
        float flow = n(vec2(vXZ.x * 2.2, vXZ.y * 0.7 - uTime * 1.6)) * 0.6 + n(vec2(vXZ.x * 5.0, vXZ.y * 1.8 - uTime * 3.1)) * 0.4;
        float streak = smoothstep(0.35, 0.85, flow);
        float shade = uShadow * min(1.0, 0.35 + t * 0.3) * (0.55 + 0.45 * streak) * step(0.01, t);
        // A branch thick enough to break a wedge: red in the shadow.
        float over = smoothstep(2.6, 3.0, t) * uShadow;
        // The snow behind the front: heavy, churning, its folds shaded blue; the crest's foot bright at the line.
        float behind = uFront > -900.0 ? smoothstep(uFront + 0.3, uFront - 0.5, vXZ.y) * smoothstep(uFront - 12.0, uFront - 3.0, vXZ.y) : 0.0;
        float churn = n(vec2(vXZ.x * 1.4, vXZ.y * 0.9 + uTime * 7.0)) * 0.6 + n(vec2(vXZ.x * 3.7, vXZ.y * 2.3 + uTime * 11.0)) * 0.4;
        float snow = behind * min(1.0, t * 0.8 + 0.2) * step(0.01, t);
        float crest = uFront > -900.0 ? smoothstep(1.4, 0.0, abs(vXZ.y - uFront + 0.3 * churn)) * step(0.01, t) : 0.0;
        vec3 veil = mix(vec3(0.26, 0.34, 0.52), vec3(0.5, 0.6, 0.78), streak);
        vec3 col = veil;
        float a = shade * 0.85;
        col = mix(col, vec3(1.0, 0.31, 0.23), over * 0.7);
        a = max(a, over * 0.45);
        // Big rolling folds (blue in their hollows) and the fine churn on them; a dark band under the crest's lip.
        float fold = n(vec2(vXZ.x * 0.55, vXZ.y * 0.45 + uTime * 3.2));
        vec3 mass = mix(vec3(0.5, 0.6, 0.78), vec3(1.0), smoothstep(0.25, 0.75, fold * 0.6 + churn * 0.4));
        float lip = uFront > -900.0 ? smoothstep(0.0, 0.8, vXZ.y - uFront) * smoothstep(2.4, 0.8, vXZ.y - uFront) * step(0.01, t) : 0.0;
        col = mix(col, mass, max(snow, crest));
        col = mix(col, vec3(0.3, 0.38, 0.52), lip * 0.6);
        a = max(a, max(max(snow * (0.86 + 0.14 * churn), crest), lip * 0.5));
        // The large slope's relief (it read as a flat board of lanes): darker and bluer toward the foot, where the snow
        // banks up in drifts across the lanes, bright at the top under the crest the avalanche breaks from.
        if (uLoose < 0.5 && vXZ.y > uTop - 1.0 && vXZ.y < uFoot + 1.0) {
          float hgt = clamp((uFoot - vXZ.y) / max(uFoot - uTop, 0.001), 0.0, 1.0);
          float drift = sin(vXZ.y * 1.9 + n(vec2(vXZ.x * 0.45, vXZ.y * 0.25)) * 3.4) * 0.5 + 0.5;
          float ripple = smoothstep(0.55, 0.95, drift) - smoothstep(0.3, 0.05, drift) * 0.5;
          float low = (1.0 - hgt) * 0.42 + max(0.0, ripple) * 0.26 * (1.0 - hgt * 0.4);
          float crestBand = smoothstep(uTop + 3.2, uTop + 1.0, vXZ.y);
          vec3 rc = mix(mix(vec3(0.34, 0.44, 0.62), vec3(0.62, 0.70, 0.84), hgt), vec3(0.98, 0.99, 1.0), crestBand);
          float ra = max(low, crestBand * 0.5);
          col = mix(rc, col, a / max(a + ra * (1.0 - a), 0.001));
          a = a + ra * (1.0 - a);
        }
        // The lee: still, colder; deeper while a front's shadow is out (where to stand).
        float still = lee * (0.1 + 0.32 * uHide);
        col = mix(col, vec3(0.22, 0.34, 0.56), still / max(a + still, 0.001));
        a = max(a * (1.0 - lee * 0.9), still);
        gl_FragColor = vec4(col, a * (1.0 - vFog * 0.5));
      }`,
  });
  return { mat, tex, data };
};

/** The front's crest: a long strip standing at the line, powder rolling up off it (only over the lanes with snow). */
const crestMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: { uTime: { value: 0 }, uLanes: { value: new Array(7).fill(1) }, uX0: { value: 0 }, uLaneW: { value: 1 }, uLoose: { value: 1 }, uK: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      varying float vX;
      void main() {
        vUv = uv;
        vec4 w = modelMatrix * vec4(position, 1.0);
        vX = w.x;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uLanes[7];
      uniform float uX0;
      uniform float uLaneW;
      uniform float uLoose;
      uniform float uK;
      varying vec2 vUv;
      varying float vX;
      // (Wrapped: with large arguments the sin hash loses precision and the noise shows in blocks, docs/RENDIMIENTO.md.)
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float t = 1.0;
        if (uLoose < 0.5) {
          float k = floor((vX - uX0) / uLaneW);
          t = (k < 0.0 || k > 6.0) ? 0.0 : min(1.0, uLanes[int(k)] * 0.7);
        }
        float billow = n(vec2(vX * 1.1, vUv.y * 1.6 - uTime * 2.4)) * 0.6 + n(vec2(vX * 2.7 + 7.0, vUv.y * 3.4 - uTime * 4.0)) * 0.4;
        float top = 0.3 + 0.62 * t * billow;
        float body = smoothstep(top, top - 0.22, vUv.y) * smoothstep(0.0, 0.04, vUv.y);
        // Powder off its top, thin and wide.
        float dust = smoothstep(top + 0.25, top - 0.05, vUv.y) * (1.0 - body) * 0.45 * billow;
        vec3 col = mix(vec3(0.52, 0.6, 0.76), vec3(1.0), smoothstep(0.1, 0.75, vUv.y + billow * 0.35));
        gl_FragColor = vec4(col, (body * 0.96 + dust) * t * uK);
      }`,
  });

export class SlidePlace {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(48);
  private glows: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number }[] = [];
  private now = 0;
  /** Where the HUD hangs its marks (world points): the rocks of the large wedges, the cave's mouth, the doors. */
  readonly anchors: { rocks: THREE.Vector3[]; mouth: THREE.Vector3 | null; doors: THREE.Vector3[] } = { rocks: [], mouth: null, doors: [] };
  private sheet: ReturnType<typeof sheetMat> | null = null;
  private crest: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial } | null = null;
  private wedges: THREE.InstancedMesh | null = null;
  private wedgeKey = '';
  private rockAt: { x: number; z: number; y: number; dir: number }[] = [];
  private lanes: { x0: number; w: number; rows: number[] } | null = null;
  private doors: { mesh: THREE.Mesh; openAt: number }[] = [];
  /** The ice cave's sentinels: the arc of their guard on the floor before each awake one (nothing gets in there). */
  private guards: THREE.Mesh[] = [];
  private stones: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; rock: THREE.Mesh }[] = [];
  private stoneState: { x: number; y: number; t: number }[] = [];
  private tickNow = 0;
  private front = { on: false, y: 0, at: -1 };
  private shadowK = 0;
  private hideK = 0;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
    kit: Kit,
  ) {
    this.group.name = `slide-${place}`;
    if (place === 'loose' || place === 'slideFar') this.buildLoose(kit, s, place === 'slideFar');
    else if (place === 'avalanche' || place === 'caveMouth') this.buildSlope(kit, s);
    else if (place === 'iceCave') this.buildCave(kit, s);
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private std(color?: THREE.ColorRepresentation, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
    const m = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0.04, flatShading: true, ...(color !== undefined ? { color } : { vertexColors: true }), ...o });
    this.mats.push(m);
    return m;
  }

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number): void {
    this.glows.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.glows.length * 1.37 });
  }

  /** A boulder in the snow: a rough rock, snow caught on its top, a smaller stone at its foot now and then. */
  private boulder(kit: Kit, geo: THREE.BufferGeometry, x: number, z: number, size: number, k: number, cap = true): void {
    const g = this.ctx.ground(x, z);
    kit.add(geo, ROCK[Math.floor(k * 3.99)] as string, place(x, g + size * 0.28, z, k * 6, new THREE.Vector3(size * (1.05 + k * 0.3), size * (0.75 + k * 0.3), size * 0.95), (k - 0.5) * 0.3, (0.5 - k) * 0.25));
    if (cap) kit.add(geo, SNOW, place(x + 0.04, g + size * (0.62 + k * 0.12), z - 0.05, k * 6 + 1, new THREE.Vector3(size * 0.8, size * 0.24, size * 0.72)));
  }

  /** The cave's mouth: an arch of blue ice cut into the slope, dark inside, icicles on its lip, facing up the slope. */
  private mouthArch(kit: Kit, x: number, z: number, wide: number, facing: number): void {
    const g = this.ctx.ground(x, z);
    const torus = new THREE.TorusGeometry(1, 0.32, 6, 12, Math.PI);
    const cone = new THREE.ConeGeometry(0.5, 1, 5);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const dodec = new THREE.DodecahedronGeometry(0.5, 0);
    // The dark inside (a deep blue, not black: light comes through the ice).
    kit.add(box, '#16233a', place(x, g + wide * 0.42, z + facing * 0.5, 0, new THREE.Vector3(wide * 1.7, wide * 0.95, 0.2)));
    // The arch, in two rings of ice.
    kit.add(torus, ICE[1] as string, place(x, g, z, 0, new THREE.Vector3(wide, wide * 1.05, 1.1)));
    kit.add(torus, ICE[2] as string, place(x, g + 0.05, z - facing * 0.25, 0, new THREE.Vector3(wide * 1.12, wide * 1.15, 0.8)));
    // Ice heaped either side and over it, the slope's snow on top.
    for (let i = -4; i <= 4; i++) {
      const a = (i / 4) * 1.4;
      const px = x + Math.sin(a) * wide * 1.45;
      const py = g + Math.cos(a) * wide * 1.15;
      const kk = Math.sin(i * 7.7) * 0.5 + 0.5;
      kit.add(dodec, ICE[(i + 8) % 4] as string, place(px, py * 0.85, z + facing * 0.3, i, new THREE.Vector3(1.0 + kk * 0.6, 0.9 + kk * 0.5, 1.2)));
      kit.add(dodec, SNOW, place(px, py * 0.85 + 0.45, z + facing * 0.2, i + 3, new THREE.Vector3(0.9, 0.3, 1.0)));
    }
    // Icicles hanging off the lip.
    for (let i = -5; i <= 5; i++) {
      const a = (i / 5) * 1.2;
      const px = x + Math.sin(a) * wide * 0.95;
      const py = g + Math.cos(a) * wide * 0.98;
      const len = 0.18 + (Math.sin(i * 3.3) * 0.5 + 0.5) * 0.3;
      kit.add(cone, ICE[2] as string, place(px, py - len * 0.5, z - facing * 0.05, 0, new THREE.Vector3(0.07, len, 0.07), Math.PI));
    }
    this.light(new THREE.Vector3(x, g + wide * 0.4, z + facing * 0.3), '#6fa6e0', wide * 2.4, 0.22);
    torus.dispose();
    cone.dispose();
    box.dispose();
    dodec.dispose();
  }

  /** The nine shields of the stretcher, their red crosses, on its poles: one kit's worth of boxes. */
  private stretcher(kit: Kit, x: number, z: number, yaw: number): void {
    const g = this.ctx.ground(x, z);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const m = place(x, g, z, yaw);
    for (const k of [-1, 1]) kit.add(box, WOOD[0] as string, m, place(k * 0.26, 0.16, 0, 0, new THREE.Vector3(0.05, 0.05, 1.6)));
    kit.add(box, WOOD[1] as string, m, place(0, 0.19, 0, 0, new THREE.Vector3(0.5, 0.03, 1.05)));
    for (let i = 0; i < 9; i++) {
      const row = Math.floor(i / 3);
      const col = (i % 3) - 1;
      const local = place(col * 0.16, 0.24 + row * 0.07, -0.35 + row * 0.35 + col * 0.02, 0, 1, -1.15, col * 0.08);
      kit.add(box, '#9aa1ab', m, new THREE.Matrix4().multiplyMatrices(local, place(0, 0, 0, 0, new THREE.Vector3(0.15, 0.24, 0.02))));
      kit.add(box, COMPANY_RED, m, new THREE.Matrix4().multiplyMatrices(local, place(0, 0, 0.012, 0, new THREE.Vector3(0.035, 0.22, 0.012))));
      kit.add(box, COMPANY_RED, m, new THREE.Matrix4().multiplyMatrices(local, place(0, 0.04, 0.012, 0, new THREE.Vector3(0.13, 0.035, 0.012))));
    }
    box.dispose();
  }

  /** The lee mask: the cells straight below a rock (the sim's own rule), for the sheet. */
  private setLee(R: number, cells: { q: number; r: number }[]): void {
    if (!this.sheet) return;
    const W = 2 * R + 1;
    const d = this.sheet.data;
    d.fill(0);
    for (const c of cells) {
      const q = c.q + R;
      const r = c.r + R;
      if (q < 0 || r < 0 || q >= W || r >= W) continue;
      d[(r * W + q) * 4] = 255;
    }
    this.sheet.tex.needsUpdate = true;
  }

  /* ---------- the loose slope ---------- */

  private buildLoose(kit: Kit, s: BattleState, far: boolean): void {
    const L = looseLayout(s.seed);
    const M = L.radius + 2;
    const dodec = new THREE.DodecahedronGeometry(0.5, 0);
    const cone = new THREE.ConeGeometry(0.5, 1, 6);
    const coverK = new Set(L.cover.map((h) => `${h.q},${h.r}`));
    // The trail's sides: scree and grey ice, bigger the farther out.
    for (const hx of L.rocks) {
      if (coverK.has(`${hx.q},${hx.r}`)) continue;
      const x2 = 2 * hx.q + hx.r;
      const mid = L.mid[hx.r + M] ?? 0;
      const off = Math.abs(x2 - mid) - 5;
      if (hx.r < -(L.radius - 1) - 1) continue;
      if (off < 1 || off > 9) continue;
      const k = Math.sin(hx.q * 12.9 + hx.r * 78.2) * 0.5 + 0.5;
      const k2 = Math.sin(hx.q * 39.3 + hx.r * 11.7) * 0.5 + 0.5;
      if (off > 2 && k2 < 0.3) continue;
      const p = this.hex(hx.q, hx.r).add(new THREE.Vector3((k2 - 0.5) * 0.9, 0, (k - 0.5) * 0.8));
      // Sparser the farther out: the trail's own edge reads, not a field of stones.
      if (off > 1 && k2 < 0.45) continue;
      const big = (0.55 + Math.min(1.8, off * 0.28)) * (0.6 + k * 0.8);
      if ((hx.q + hx.r * 3) % 4 === 0) kit.add(cone, ICE[(hx.q + 9) % 4] as string, place(p.x, p.y + big * 0.35, p.z, k * 6, new THREE.Vector3(big * 0.55, big * 1.2, big * 0.45), 0.2 * (k - 0.5), 0.3 * (k - 0.5)));
      else this.boulder(kit, dodec, p.x, p.z, big, k, k2 > 0.35);
    }
    // The trail: trodden snow up the middle, a little greyer, the way Kel hauls the stretcher.
    const tbox = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i + 1 < L.way.length; i++) {
      const a = L.way[i] as { x2: number; r: number };
      const b = L.way[i + 1] as { x2: number; r: number };
      const ax = a.x2 * 0.5 * S;
      const az = a.r * ROW;
      const bx = b.x2 * 0.5 * S;
      const bz = b.r * ROW;
      const mx = (ax + bx) / 2;
      const mz = (az + bz) / 2;
      const len = Math.hypot(bx - ax, bz - az);
      kit.add(tbox, '#c7d1de', place(mx, this.ctx.ground(mx, mz) + 0.012, mz, Math.atan2(bx - ax, bz - az), new THREE.Vector3(1.3, 0.02, len + 0.2)));
    }
    tbox.dispose();
    // The cover: big boulders on the trail, dark, snow-capped, the snow piled against their uphill side.
    for (const hx of L.cover) {
      const p = this.hex(hx.q, hx.r);
      const k = Math.sin(hx.q * 4.1 + hx.r * 9.3) * 0.5 + 0.5;
      this.boulder(kit, dodec, p.x, p.z, 1.15 + k * 0.25, k);
      kit.add(dodec, SNOW_SHADE, place(p.x, p.y + 0.1, p.z - 0.55, 0, new THREE.Vector3(1.1, 0.35, 0.6)));
    }
    // The ice cave's mouth at the trail's top.
    const top = L.mouth;
    const mz = top.r * ROW;
    const mx = top.x2 * 0.5 * S;
    this.mouthArch(kit, mx, mz - 0.9, 1.0, 1);
    // Above it the ice wall, rising into the pass.
    for (let i = -7; i <= 7; i++) {
      const k = Math.sin(i * 5.3) * 0.5 + 0.5;
      const x = mx + i * 0.95;
      const z = mz - 2.2 - k * 0.8;
      kit.add(cone, ICE[(i + 14) % 4] as string, place(x, this.ctx.ground(x, z) + 1.2 + k, z, i, new THREE.Vector3(1.1 + k * 0.6, 3.0 + k * 2.0, 0.9)));
    }
    this.anchors.mouth = new THREE.Vector3(mx, this.ctx.ground(mx, mz) + 2.4, mz - 0.8);
    if (far) {
      // The crack: a dark line opening across the snow high up the slope.
      const box = new THREE.BoxGeometry(1, 1, 1);
      for (let i = -9; i <= 9; i++) {
        const x = i * 0.8;
        const z = -6.2 * ROW + Math.sin(i * 0.9) * 0.35;
        kit.add(box, '#4c5f7c', place(x, this.ctx.ground(x, z) + 0.06, z, Math.sin(i) * 0.3, new THREE.Vector3(0.85, 0.05, 0.09)));
      }
      box.dispose();
    }
    this.sheet = sheetMat(L.radius);
    this.track(this.sheet.mat);
    const lee: { q: number; r: number }[] = [];
    for (const c of L.cover) lee.push({ q: c.q, r: c.r + 1 }, { q: c.q - 1, r: c.r + 1 });
    this.setLee(L.radius, lee);
    const u = this.sheet.mat.uniforms;
    (u.uTop as THREE.IUniform<number>).value = -(L.radius + 1) * ROW;
    (u.uFoot as THREE.IUniform<number>).value = (L.radius + 1) * ROW;
    this.buildCrest(-(L.radius + 1) * S, (L.radius + 1) * S, true);
    dodec.dispose();
    cone.dispose();
  }

  /* ---------- the large wedges' slope ---------- */

  private buildSlope(kit: Kit, s: BattleState): void {
    const L = avalancheLayout(s.seed);
    const dodec = new THREE.DodecahedronGeometry(0.5, 0);
    const cone = new THREE.ConeGeometry(0.5, 1, 6);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const x0 = L.x0 * K;
    const lw = L.laneW * K;
    const top = -(L.radius + 0.5) * ROW;
    const foot = L.foot * K;
    // The crest at the slope's top: a lip of packed snow the avalanche breaks from, its underside in blue shadow.
    for (let x = x0 - 0.4; x < x0 + 7 * lw + 0.4; x += 0.85) {
      const k = Math.sin(x * 2.3) * 0.5 + 0.5;
      kit.add(dodec, '#9fb0c8', place(x, this.ctx.ground(x, top) + 0.25, top - 0.15, k * 3, new THREE.Vector3(1.0, 0.5, 0.9)));
      kit.add(dodec, SNOW, place(x + 0.1, this.ctx.ground(x, top) + 0.55, top - 0.35, k * 5, new THREE.Vector3(1.15, 0.45, 1.1)));
    }
    // The furrows between the lanes: shallow grooves down the slope, blue-grey.
    for (let i = 1; i < 7; i++) {
      const x = x0 + i * lw;
      for (let z = top; z < foot - 0.4; z += 0.55) kit.add(box, '#a8b6c9', place(x + Math.sin(z * 1.3 + i) * 0.04, this.ctx.ground(x, z) + 0.015, z, 0, new THREE.Vector3(0.07, 0.03, 0.5)));
    }
    // The void at the sides: the edge broken off in rock and ice, dark below.
    const edge = x0 - 0.15;
    for (const side of [-1, 1]) {
      const ex = side < 0 ? edge : -edge;
      for (let z = top - 1; z < foot + 1.5; z += 0.7) {
        const k = Math.sin(z * 3.7 + side) * 0.5 + 0.5;
        const x = ex + side * (0.4 + k * 0.5);
        this.boulder(kit, dodec, x, z, 0.7 + k * 0.7, k, k > 0.4);
        // Past the edge, the broken lip going down: dark rock, ice in its cracks.
        if (k > 0.55) kit.add(cone, ICE_DEEP[Math.floor(k * 3.99)] as string, place(ex + side * (1.5 + k), this.ctx.ground(ex, z) - 0.2, z, k * 4, new THREE.Vector3(0.6, 1.4, 0.5), 0, side * 0.5));
      }
    }
    // The cliff the cave is cut into, round its mouth at the foot.
    for (const hx of L.cliff) {
      const p = this.hex(hx.q, hx.r);
      const k = Math.sin(hx.q * 3.3 + hx.r * 7.1) * 0.5 + 0.5;
      if ((hx.q + hx.r) % 3 === 0) kit.add(cone, ICE[(hx.q + 12) % 4] as string, place(p.x, p.y + 0.9, p.z, k * 5, new THREE.Vector3(0.9, 2.2 + k, 0.8)));
      else this.boulder(kit, dodec, p.x, p.z, 1.2 + k * 0.5, k);
    }
    const mouth = new THREE.Vector3(L.mouth.x * K, 0, L.mouth.y * K);
    this.mouthArch(kit, mouth.x, mouth.z + 0.3, 1.45, -1);
    this.stretcher(kit, mouth.x + 0.55, mouth.z + 0.75, 0.3);
    this.anchors.mouth = new THREE.Vector3(mouth.x, this.ctx.ground(mouth.x, mouth.z) + 2.5, mouth.z + 0.2);
    // The nine rocks, their grain (a band of quartz leaning the way it turns the snow) and the chevron under them.
    this.rockAt = [];
    for (const row of L.rows)
      for (const rk of row.rocks) {
        const x = rk.x * K;
        const z = rk.y * K;
        const g = this.ctx.ground(x, z);
        const k = Math.sin(x * 2.1 + z * 1.7) * 0.5 + 0.5;
        kit.add(dodec, ROCK[1] as string, place(x, g + 0.42, z, k, new THREE.Vector3(1.35, 1.05, 1.15), 0, 0));
        kit.add(dodec, SNOW, place(x, g + 0.86, z - 0.1, k + 1, new THREE.Vector3(0.95, 0.26, 0.8)));
        // The grain: two pale bands across the face toward the camera, leaning down to the side it turns the snow.
        for (const off of [-0.12, 0.12])
          kit.add(box, QUARTZ, place(x + off * 0.4, g + 0.45 + off, z + 0.52, 0, new THREE.Vector3(1.05, 0.07, 0.06), 0, -rk.dir * 0.55));
        // The chevron in the snow below, pointing the same way.
        for (const sgn of [-1, 1]) kit.add(box, '#7f93ad', place(x + rk.dir * 0.12, g + 0.02, z + 1.0 + sgn * 0.12, rk.dir * sgn * 0.7 + Math.PI / 2, new THREE.Vector3(0.05, 0.03, 0.42)));
        this.rockAt.push({ x, z, y: g, dir: rk.dir });
        this.anchors.rocks.push(new THREE.Vector3(x, g + 1.6, z));
      }
    this.lanes = { x0, w: lw, rows: L.rows.map((r) => r.y * K) };
    // The wedges: one instanced mesh (an iron wedge with a red rag tied on), placed when driven in.
    const wk = new Kit();
    wk.add(new THREE.ConeGeometry(0.11, 0.62, 4), IRON, place(0, 0, 0, Math.PI / 4, new THREE.Vector3(1, 1, 0.5), 0, Math.PI / 2));
    wk.add(new THREE.BoxGeometry(0.2, 0.12, 0.16), '#2a2d33', place(0.33, 0, 0));
    wk.add(new THREE.BoxGeometry(0.06, 0.2, 0.08), COMPANY_RED, place(0.28, -0.08, 0.02, 0, 1, 0.2));
    const wm = wk.build(this.std(undefined, { metalness: 0.4, roughness: 0.6 }), 'slide-wedge');
    if (wm) {
      this.geos.push(wm.geometry);
      const inst = new THREE.InstancedMesh(wm.geometry, wm.material as THREE.Material, 9);
      inst.count = 0;
      inst.castShadow = true;
      inst.name = 'slide-wedges';
      inst.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(27).fill(1), 3);
      this.group.add(inst);
      this.wedges = inst;
    }
    this.sheet = sheetMat(L.radius);
    this.track(this.sheet.mat);
    const lee: { q: number; r: number }[] = [];
    for (const row of L.rows) for (const rk of row.rocks) lee.push({ q: rk.cell.q, r: rk.cell.r + 1 }, { q: rk.cell.q - 1, r: rk.cell.r + 1 });
    this.setLee(L.radius, lee);
    const u = this.sheet.mat.uniforms;
    (u.uLoose as THREE.IUniform<number>).value = 0;
    (u.uX0 as THREE.IUniform<number>).value = x0;
    (u.uLaneW as THREE.IUniform<number>).value = lw;
    (u.uRows as THREE.IUniform<THREE.Vector3>).value.set(L.rows[0]!.y * K, L.rows[1]!.y * K, L.rows[2]!.y * K);
    (u.uTop as THREE.IUniform<number>).value = top;
    (u.uFoot as THREE.IUniform<number>).value = foot;
    this.setFlow(laneFlow(L.rows.map((r) => r.rocks), new Array(9).fill(0)).seg);
    this.buildCrest(x0, x0 + 7 * lw, false);
    // The giant's rocks: their shadows and the stones that land in them (a pool, as at the fort).
    const shGeo = this.track(new THREE.CircleGeometry(1, 24));
    const rockGeo = this.track(new THREE.DodecahedronGeometry(0.4, 0));
    const rockMat = this.std(ROCK[2]);
    for (let i = 0; i < 4; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: '#1a2230', transparent: true, opacity: 0, depthWrite: false });
      this.mats.push(mat);
      const mesh = new THREE.Mesh(shGeo, mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.renderOrder = 4;
      mesh.visible = false;
      this.group.add(mesh);
      const rock = new THREE.Mesh(rockGeo, rockMat);
      rock.visible = false;
      rock.castShadow = true;
      this.group.add(rock);
      this.stones.push({ mesh, mat, rock });
    }
    dodec.dispose();
    cone.dispose();
    box.dispose();
  }

  /** The front's crest strip, from x `a` to `b` (world), hidden until a front comes down. */
  private buildCrest(a: number, b: number, loose: boolean): void {
    const mat = crestMat();
    this.track(mat);
    (mat.uniforms.uLoose as THREE.IUniform<number>).value = loose ? 1 : 0;
    if (this.lanes) {
      (mat.uniforms.uX0 as THREE.IUniform<number>).value = this.lanes.x0;
      (mat.uniforms.uLaneW as THREE.IUniform<number>).value = this.lanes.w;
    }
    const geo = this.track(new THREE.PlaneGeometry(b - a, 2.8, 48, 1));
    geo.translate((a + b) / 2, 1.3, 0);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.visible = false;
    mesh.frustumCulled = false;
    mesh.renderOrder = 6;
    mesh.name = 'slide-crest';
    this.group.add(mesh);
    this.crest = { mesh, mat };
  }

  private setFlow(seg: number[][]): void {
    if (!this.sheet) return;
    const arr = (this.sheet.mat.uniforms.uLanes as THREE.IUniform<number[]>).value;
    for (let i = 0; i < 4; i++) for (let k = 0; k < 7; k++) arr[i * 7 + k] = (seg[i] as number[])[k] as number;
  }

  /* ---------- the ice cave ---------- */

  private buildCave(kit: Kit, s: BattleState): void {
    const L = caveLayout(s.seed);
    const cone = new THREE.ConeGeometry(0.5, 1, 6);
    const prism = new THREE.CylinderGeometry(0.42, 0.5, 1, 6);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const dodec = new THREE.DodecahedronGeometry(0.5, 0);
    const floor = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    const near = (q: number, r: number): boolean => [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]].some(([dq, dr]) => floor.has(`${q + (dq as number)},${r + (dr as number)}`));
    // The ice: every cell that isn't floor, walls by the floor tall and clear, farther ones a deep blue mass.
    for (const hx of L.rocks) {
      const p = this.hex(hx.q, hx.r);
      const k = Math.sin(hx.q * 12.9 + hx.r * 78.2) * 0.5 + 0.5;
      const k2 = Math.sin(hx.q * 3.9 + hx.r * 5.7) * 0.5 + 0.5;
      if (near(hx.q, hx.r)) {
        // By the floor: clusters of ice crystals, tall and tilted, pale toward the room, a deep blue foot.
        kit.add(prism, ICE_DEEP[Math.floor(k2 * 3.99)] as string, place(p.x, p.y + 0.55, p.z, k * 3, new THREE.Vector3(1.05, 1.1, 1.0)));
        for (let c = 0; c < 3; c++) {
          const a = k * 6 + c * 2.1;
          const hgt = 1.2 + ((k2 + c * 0.37) % 1) * 1.6;
          kit.add(prism, ICE[(Math.floor(k * 3.99) + c) % 4] as string, place(p.x + Math.cos(a) * 0.3, p.y + hgt * 0.5 + 0.3, p.z + Math.sin(a) * 0.3, a, new THREE.Vector3(0.42, hgt, 0.42), Math.cos(a) * 0.22, Math.sin(a) * 0.22));
          kit.add(cone, ICE[2] as string, place(p.x + Math.cos(a) * 0.3 + Math.sin(a) * 0.22 * hgt * 0.5, p.y + hgt + 0.3 + 0.2, p.z + Math.sin(a) * 0.3, a, new THREE.Vector3(0.42, 0.45, 0.42), Math.cos(a) * 0.22, Math.sin(a) * 0.22));
        }
        // Icicles off the wall's lip, now and then.
        if (k2 > 0.55) kit.add(cone, ICE[2] as string, place(p.x + 0.3, p.y + 1.55, p.z + 0.45, 0, new THREE.Vector3(0.08, 0.4, 0.08), Math.PI));
      } else kit.add(prism, ICE_DEEP[Math.floor(k * 3.99)] as string, place(p.x, p.y + 1.3 + k2 * 0.5, p.z, k * 3, new THREE.Vector3(1.25, 2.6 + k2, 1.25)));
    }
    // Frost and loose ice on the floor, a few frozen stones.
    for (const hx of L.floor) {
      const k = Math.sin(hx.q * 7.3 + hx.r * 3.1) * 0.5 + 0.5;
      if (k < 0.82) continue;
      const p = this.hex(hx.q, hx.r);
      kit.add(dodec, ICE[3] as string, place(p.x + (k - 0.9) * 2, p.y + 0.08, p.z, k * 7, new THREE.Vector3(0.35, 0.16, 0.3)));
    }
    // Cold lights in the ice, by each room and the hall.
    for (const rm of L.rooms) this.light(this.hex(rm.at.q, rm.at.r, 0.6), '#5d9ae0', 6.5, 0.16);
    this.light(this.hex(L.hall.q, L.hall.r, 0.6), '#8fc4ff', 4.5, 0.24);
    // The doors: a slab of ice each, cracked open when its room is cleared.
    const doorGeo = this.track(new THREE.BoxGeometry(1.15, 1.9, 0.5));
    for (const d of L.doors) {
      const mat = new THREE.MeshStandardMaterial({ color: ICE[0], roughness: 0.25, metalness: 0.05, transparent: true, opacity: 0.88, flatShading: true });
      this.mats.push(mat);
      const mesh = new THREE.Mesh(doorGeo, mat);
      mesh.position.copy(this.hex(d.q, d.r, 0.95));
      mesh.castShadow = true;
      mesh.name = 'slide-door';
      this.group.add(mesh);
      this.doors.push({ mesh, openAt: -1 });
      this.anchors.doors.push(this.hex(d.q, d.r, 2.2));
    }
    // The guards' arcs: a red fan on the floor in front of each awake sentinel, as wide as its guard (a pool).
    const arc = ((s.rules?.senda?.cave?.arc ?? 200) / 1024) * Math.PI * 2;
    const fan = this.track(new THREE.RingGeometry(0.35, 1.25, 20, 1, -arc, arc * 2));
    fan.rotateX(-Math.PI / 2);
    const fanMat = new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0.32, depthWrite: false });
    this.mats.push(fanMat);
    for (let i = 0; i < 6; i++) {
      const m = new THREE.Mesh(fan, fanMat);
      m.visible = false;
      m.renderOrder = 4;
      m.name = 'slide-guard';
      this.group.add(m);
      this.guards.push(m);
    }
    cone.dispose();
    prism.dispose();
    box.dispose();
    dodec.dispose();
  }

  /** The board's ground, for the sheet: the shadow, the snow, the lee painted on the terrain's own mesh. */
  sheetOn(src: THREE.Mesh): void {
    if (!this.sheet) return;
    const sheet = new THREE.Mesh(src.geometry, this.sheet.mat);
    sheet.matrixAutoUpdate = false;
    src.updateMatrixWorld(true);
    sheet.matrix.copy(src.matrixWorld);
    sheet.renderOrder = 3;
    sheet.name = 'slide-sheet';
    sheet.frustumCulled = false;
    this.group.add(sheet);
  }

  /** A tick's events: the front's start, its strikes, the mouth touched, a door cracked open. */
  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      if (e.kind === 'front') this.ctx.shake(0.22);
      else if (e.kind === 'touch') this.ctx.shake(0.55);
      else if (e.kind === 'split') this.ctx.shake(0.25);
      else if (e.kind === 'rock') this.ctx.shake(0.15);
      else if (e.kind === 'open') {
        const d = this.doors[e.n ?? 0];
        if (d && d.openAt < 0) {
          d.openAt = this.now;
          this.ctx.shake(0.2);
        }
      }
    }
  }

  /** Every drawn frame: the wedges in their rocks, the shadow's lanes, the front's line, the giant's rocks, the doors. */
  draw(s: BattleState): void {
    const w = s.senda?.slide;
    const r = s.rules?.senda?.slide;
    this.tickNow = s.tick;
    if (w && r) {
      this.front.on = w.on;
      this.front.y = w.y * K;
      // The shadow: out all through the large wedges (stronger as the fall comes), and on the loose slope only before a front.
      const shadow = slideShadow(s, r, w);
      const left = w.next >= 0 ? Math.max(0, w.next - s.tick) : 9999;
      this.shadowK = r.lanes ? (w.over >= 0 ? 0 : 0.55 + 0.45 * Math.max(0, 1 - left / r.warn)) : shadow ? Math.min(1, 1 - left / Math.max(1, r.warn) + 0.25) : 0;
      this.hideK = shadow ? 1 : 0;
      if (r.lanes) {
        const key = w.shut.join('');
        if (key !== this.wedgeKey) {
          this.wedgeKey = key;
          const f = laneFlow(r.lanes.rows.map((x) => x.rocks), w.shut);
          this.setFlow(f.seg);
          this.placeWedges(w.shut, f.broke);
        }
        this.stoneState = w.stones;
      }
    }
    const cv = s.senda?.cave;
    if (cv && this.guards.length) {
      let n = 0;
      cv.ids.forEach((ids, i) => {
        if (!cv.awake[i]) return;
        for (const id of ids) {
          const e = s.enemies.find((x) => x.id === id && x.hp > 0);
          const g = this.guards[n];
          if (!e || !g) continue;
          g.position.copy(this.ctx.at(e.x, e.y, 0.05));
          g.rotation.y = -(e.facing / 1024) * Math.PI * 2;
          g.visible = true;
          n++;
        }
      });
      for (let i = n; i < this.guards.length; i++) (this.guards[i] as THREE.Mesh).visible = false;
    }
    if (cv) cv.cleared.forEach((c, i) => {
      const d = this.doors[i];
      if (c && d && d.openAt < 0) d.openAt = this.now - 5;
    });
  }

  private placeWedges(shut: number[], broke: boolean[]): void {
    const inst = this.wedges;
    if (!inst) return;
    const m = new THREE.Matrix4();
    const c = new THREE.Color();
    let n = 0;
    shut.forEach((x, i) => {
      if (x !== 1) return;
      const rk = this.rockAt[i];
      if (!rk) return;
      // Driven into the rock's face toward the camera, on the side the grain leans down to.
      m.copy(place(rk.x + rk.dir * 0.35, rk.y + 0.38, rk.z + 0.62, rk.dir > 0 ? 0.35 : Math.PI - 0.35, 1.35, 0, 0));
      inst.setMatrixAt(n, m);
      inst.setColorAt(n, broke[i] ? c.set('#ff6a4a') : c.set('#ffffff'));
      n++;
    });
    inst.count = n;
    inst.instanceMatrix.needsUpdate = true;
    if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
  }

  frame(now: number): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    if (this.sheet) {
      const u = this.sheet.mat.uniforms;
      (u.uTime as THREE.IUniform<number>).value = now % 120;
      const sh = u.uShadow as THREE.IUniform<number>;
      sh.value += (this.shadowK - sh.value) * Math.min(1, dt * 3);
      const hd = u.uHide as THREE.IUniform<number>;
      hd.value += (this.hideK - hd.value) * Math.min(1, dt * 4);
      (u.uFront as THREE.IUniform<number>).value = this.front.on ? this.front.y : -999;
    }
    if (this.crest) {
      const m = this.crest.mat.uniforms;
      (m.uTime as THREE.IUniform<number>).value = now % 120;
      const k = m.uK as THREE.IUniform<number>;
      k.value += ((this.front.on ? 1 : 0) - k.value) * Math.min(1, dt * 6);
      this.crest.mesh.visible = k.value > 0.02;
      if (this.front.on) this.crest.mesh.position.set(0, this.ctx.ground(0, this.front.y), this.front.y);
      // Leaning downhill toward the camera, so the crest reads from the steep game view too.
      this.crest.mesh.rotation.x = -0.55;
      // Which lanes carry snow at the line (the crest's height by lane).
      if (this.sheet && this.lanes) {
        const arr = (this.sheet.mat.uniforms.uLanes as THREE.IUniform<number[]>).value;
        const seg = this.front.y < (this.lanes.rows[0] ?? 0) ? 0 : this.front.y < (this.lanes.rows[1] ?? 0) ? 1 : this.front.y < (this.lanes.rows[2] ?? 0) ? 2 : 3;
        const out = (m.uLanes as THREE.IUniform<number[]>).value;
        for (let i = 0; i < 7; i++) out[i] = arr[seg * 7 + i] as number;
      }
    }
    // The doors: the ice cracks (a shiver), then sinks and fades.
    for (const d of this.doors) {
      if (d.openAt < 0) continue;
      const k = now - d.openAt;
      const mat = d.mesh.material as THREE.MeshStandardMaterial;
      const sink = Math.min(1, Math.max(0, (k - 0.5) / 1.2));
      d.mesh.position.x += k < 0.5 ? Math.sin(now * 60) * 0.004 : 0;
      d.mesh.scale.y = Math.max(0.02, 1 - sink);
      mat.opacity = 0.88 * (1 - sink);
      d.mesh.visible = sink < 1;
    }
    // The giant's rocks: the shadow darker and smaller as the stone comes; it lands in the last half second.
    this.stones.forEach((sh, i) => {
      const x = this.stoneState[i];
      if (!x) {
        sh.mesh.visible = false;
        sh.rock.visible = false;
        return;
      }
      const left = Math.max(0, (x.t - this.tickNow) / 12);
      const p = this.ctx.at(x.x, x.y, 0.04);
      const k = 1 - Math.min(1, left / 2);
      sh.mesh.position.copy(p);
      sh.mesh.scale.setScalar(0.95 - k * 0.25);
      sh.mat.opacity = 0.15 + k * 0.5;
      sh.mesh.visible = true;
      sh.rock.visible = left < 0.7;
      sh.rock.position.set(p.x - left * 6, p.y + left * 10, p.z - left * 4);
      sh.rock.rotation.set(now * 3 + i, now * 2, 0);
    });
    this.glow.begin();
    for (const gl of this.glows) {
      const f = 0.9 + Math.sin(now * 1.3 + gl.seed) * 0.1;
      this.glow.add(gl.p.x, gl.p.y, gl.p.z, gl.c, gl.size, gl.a * f);
    }
    this.glow.end();
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.sheet?.tex.dispose();
    this.glow.mesh.geometry.dispose();
  }
}
